// Typed wrappers around `/api/exhibits/*`.
//
// One-liners over the shared axios client. They exist so consumers (custom
// hooks in PR 10 ST 7, components in PR 11+) get full type checking against
// the OpenAPI-generated schema instead of stringly-typed axios calls.

import { getApiClient } from "./client";
import type { ListStatus } from "./listStatus";
import type { components } from "./types";

export type Exhibit = components["schemas"]["ExhibitResponse"];
export type ExhibitListItem = components["schemas"]["ExhibitList"];
export type ExhibitCreate = components["schemas"]["ExhibitCreate"];
export type ExhibitUpdate = components["schemas"]["ExhibitUpdate"];
export type ExhibitTransitionRequest =
  components["schemas"]["ExhibitTransitionRequest"];
export type ImageUploadResponse = components["schemas"]["ImageUploadResponse"];
export type QrGenerationResponse =
  components["schemas"]["QrGenerationResponse"];
export type CodeRegenerationResponse =
  components["schemas"]["CodeRegenerationResponse"];
export type MediaItem = components["schemas"]["MediaItem"];
export type AssetKind = components["schemas"]["AssetKind"];
export type SetNarrationRequest = components["schemas"]["SetNarrationRequest"];
export type NarrationPreviewResponse =
  components["schemas"]["NarrationPreviewResponse"];
export type NarrationSource = components["schemas"]["NarrationSource"];
export type AssignZoneRequest = components["schemas"]["AssignZoneRequest"];

export type ExhibitMembership = "unassigned" | "taken";

export interface ExhibitSearchOptions {
  /** Case-insensitive substring over code + titles (server-side, F-R). */
  q?: string;
  /** Newest-first cap, 1–500. */
  limit?: number;
  /** Picker tab, server-side: `unassigned` = in no zone; `taken` = held by
   *  a zone other than `zoneId`. */
  membership?: ExhibitMembership;
  zoneId?: string;
}

export async function listExhibits(
  status?: ListStatus,
  options: ExhibitSearchOptions = {},
): Promise<ExhibitListItem[]> {
  const params: Record<string, string | number> = {};
  if (status) params.status = status;
  if (options.q?.trim()) params.q = options.q.trim();
  if (options.limit) params.limit = options.limit;
  if (options.membership) params.membership = options.membership;
  if (options.membership === "taken" && options.zoneId)
    params.zone_id = options.zoneId;
  const { data } = await getApiClient().get<ExhibitListItem[]>("/exhibits", {
    params,
  });
  return data;
}

/** F-R (2026-09-08): the zone's linked exhibits in visitor order, purged
 *  tombstones included — independent of how many exhibits exist. */
/** Totals behind the picker's Available / Taken chips (scope-aware). */
export interface ExhibitMembershipCounts {
  unassigned: number;
  taken: number;
}

export async function getExhibitMembershipCounts(
  zoneId?: string,
): Promise<ExhibitMembershipCounts> {
  const { data } = await getApiClient().get<ExhibitMembershipCounts>(
    "/exhibits/membership-counts",
    { params: zoneId ? { zone_id: zoneId } : {} },
  );
  return data;
}

export async function listZoneExhibits(
  zoneId: string,
): Promise<ExhibitListItem[]> {
  const { data } = await getApiClient().get<ExhibitListItem[]>(
    `/staff/zones/${zoneId}/exhibits`,
  );
  return data;
}

/** Row for the printable QR board — the only list shape that still carries
 *  the base64 QR image. Inline type until the next `regen-types.sh`. */
export interface ExhibitBoardRow {
  id: string;
  code: string;
  titleTc: string;
  coverImageId: string | null;
  qrCodeUrl: string | null;
  state: "draft" | "published" | "archived" | "purged";
  zoneId: string | null;
}

export async function listExhibitionExhibits(
  exhibitionId: string,
): Promise<ExhibitBoardRow[]> {
  const { data } = await getApiClient().get<ExhibitBoardRow[]>(
    `/staff/exhibitions/${exhibitionId}/exhibits`,
  );
  return data;
}

export async function getExhibit(exhibitId: string): Promise<Exhibit> {
  const { data } = await getApiClient().get<Exhibit>(`/exhibits/${exhibitId}`);
  return data;
}

// F-C: dedicated staff fetch (visible regardless of state — archived
// + purged exhibits still resolve so the form can render the
// lockdown banner + read-only content). The visitor `/exhibits/{id}`
// path 404s on non-published, which was breaking the staff form
// for cascade-archived rows.
export async function getStaffExhibit(exhibitId: string): Promise<Exhibit> {
  const { data } = await getApiClient().get<Exhibit>(
    `/staff/exhibits/${exhibitId}`,
  );
  return data;
}

export async function createExhibit(payload: ExhibitCreate): Promise<Exhibit> {
  const { data } = await getApiClient().post<Exhibit>("/exhibits", payload);
  return data;
}

export async function updateExhibit(
  exhibitId: string,
  payload: ExhibitUpdate,
): Promise<Exhibit> {
  const { data } = await getApiClient().put<Exhibit>(
    `/exhibits/${exhibitId}`,
    payload,
  );
  return data;
}

// Move the exhibit into a zone; `zoneId: null` unlinks (admin-only —
// the backend 422s `exhibit.move.unlink_admin_only` for editors).
export async function assignExhibitZone(
  exhibitId: string,
  zoneId: string | null,
): Promise<Exhibit> {
  const payload: AssignZoneRequest = { zoneId };
  const { data } = await getApiClient().put<Exhibit>(
    `/staff/exhibits/${exhibitId}/zone`,
    payload,
  );
  return data;
}

export async function transitionExhibit(
  exhibitId: string,
  payload: ExhibitTransitionRequest,
): Promise<Exhibit> {
  const { data } = await getApiClient().patch<Exhibit>(
    `/exhibits/${exhibitId}/transition`,
    payload,
  );
  return data;
}

export async function deleteExhibit(exhibitId: string): Promise<void> {
  await getApiClient().delete(`/exhibits/${exhibitId}`);
}

export async function uploadExhibitImage(
  exhibitId: string,
  file: File,
): Promise<ImageUploadResponse> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await getApiClient().post<ImageUploadResponse>(
    `/exhibits/${exhibitId}/upload-image`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
}

// ── F-F media + narration (staff) ──────────────────────────────────────
// Narration precedence is resolved on the visitor side; these staff calls
// just mutate the row. URLs hit the `/staff/...` namespace so they work on
// non-published exhibits (mirrors getStaffExhibit). Wire shape is snake_case
// per ADR-0020.

// Point narration_asset_id_<language> at an existing asset, or clear it
// (asset_id: null) to fall back to voice_url / browser TTS.
export async function setExhibitNarration(
  exhibitId: string,
  language: string,
  payload: SetNarrationRequest,
): Promise<Exhibit> {
  const { data } = await getApiClient().put<Exhibit>(
    `/staff/exhibits/${exhibitId}/narration/${language}`,
    payload,
  );
  return data;
}

// Synchronously generate MiniMax narration for the description in `language`
// (ADR-0024 D2 v1) and point the narration ref at the new AUDIO asset.
export async function generateExhibitNarration(
  exhibitId: string,
  language: string,
): Promise<Exhibit> {
  const { data } = await getApiClient().post<Exhibit>(
    `/staff/exhibits/${exhibitId}/narration/${language}/generate`,
  );
  return data;
}

// F-O: generate a DRAFT preview from custom text — no narration slot is
// touched; confirm by passing the returned assetId to setExhibitNarration,
// abandon via discardExhibitNarrationPreview.
export async function previewExhibitNarration(
  exhibitId: string,
  language: string,
  text: string,
): Promise<string> {
  const { data } = await getApiClient().post<NarrationPreviewResponse>(
    `/staff/exhibits/${exhibitId}/narration/${language}/preview`,
    { text },
  );
  return data.assetId;
}

export async function discardExhibitNarrationPreview(
  exhibitId: string,
  assetId: string,
): Promise<void> {
  await getApiClient().delete(
    `/staff/exhibits/${exhibitId}/narration-previews/${assetId}`,
  );
}

// Upload a custom audio file as the narration for `language` (replaces).
export async function uploadExhibitNarration(
  exhibitId: string,
  language: string,
  file: File,
): Promise<Exhibit> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await getApiClient().post<Exhibit>(
    `/staff/exhibits/${exhibitId}/narration/${language}`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
}

// Upload a supporting media file (audio/video/pdf/image) → append to media[].
export async function uploadExhibitMedia(
  exhibitId: string,
  params: {
    file: File;
    kind: AssetKind;
    language?: string | null;
    caption?: string | null;
  },
): Promise<Exhibit> {
  const form = new FormData();
  form.append("file", params.file);
  form.append("kind", params.kind);
  if (params.language) form.append("language", params.language);
  if (params.caption) form.append("caption", params.caption);
  const { data } = await getApiClient().post<Exhibit>(
    `/staff/exhibits/${exhibitId}/media/upload`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
}

// Edit a media item's caption + language after upload (F-F.3). Both fields
// are sent each time; null clears.
export async function updateExhibitMedia(
  exhibitId: string,
  assetId: string,
  payload: { caption: string | null; language: string | null },
): Promise<Exhibit> {
  const { data } = await getApiClient().patch<Exhibit>(
    `/staff/exhibits/${exhibitId}/media/${assetId}`,
    payload,
  );
  return data;
}

// Detach an asset from media[] (asset soft-deletes per ADR-0021).
export async function removeExhibitMedia(
  exhibitId: string,
  assetId: string,
): Promise<Exhibit> {
  const { data } = await getApiClient().delete<Exhibit>(
    `/staff/exhibits/${exhibitId}/media/${assetId}`,
  );
  return data;
}

export async function reorderExhibitMedia(
  exhibitId: string,
  assetIds: string[],
): Promise<Exhibit> {
  const { data } = await getApiClient().put<Exhibit>(
    `/staff/exhibits/${exhibitId}/media/reorder`,
    { assetIds },
  );
  return data;
}

export async function generateExhibitQR(
  exhibitId: string,
): Promise<QrGenerationResponse> {
  const { data } = await getApiClient().post<QrGenerationResponse>(
    `/exhibits/${exhibitId}/generate-qr`,
  );
  return data;
}

export async function regenerateExhibitCode(
  exhibitId: string,
): Promise<CodeRegenerationResponse> {
  const { data } = await getApiClient().post<CodeRegenerationResponse>(
    `/exhibits/${exhibitId}/regenerate-code`,
  );
  return data;
}
